/**
 * Список клиентов с фильтрами (ADMIN_SPEC §6.2, дизайн 02_clients).
 *
 * Живой источник — GET /admin/clients: type/status/query/tariffs/page
 * уезжают как есть, города UI держит именами — в id их переводит
 * справочник getCities. Мок-ветка имитирует те же серверные фильтры.
 */

import {apiEnabled, http} from '@services/utils';

import {getCities} from '../getCities';
import {MOCK_CLIENTS} from '../mocks';
import type {ClientListItem, ClientsResponse, GetClientsRequest} from '../types';

import {toClientListItem} from './toClientListItem';

const PAGE_SIZE = 25;
const FAKE_LATENCY_MS = 240;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const matchesQuery = (c: ClientListItem, q: string): boolean => {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  const phone = c.adminPhone.replace(/\s/g, '');
  return (
    c.name.toLowerCase().includes(needle) ||
    c.adminFullName.toLowerCase().includes(needle) ||
    phone.includes(needle.replace(/\s/g, ''))
  );
};

const getClientsMock = async (req: GetClientsRequest): Promise<ClientsResponse> => {
  await sleep(FAKE_LATENCY_MS);
  const page = Math.max(1, req.page ?? 1);
  const cities = req.cities ?? [];
  const tariffs = req.tariffs ?? [];

  const filtered = MOCK_CLIENTS.filter(
    (c) =>
      (!req.type || c.type === req.type) &&
      (!req.status || c.subscriptionStatus === req.status) &&
      (cities.length === 0 || cities.includes(c.cityName)) &&
      (tariffs.length === 0 || tariffs.includes(c.tariffName)) &&
      matchesQuery(c, req.query ?? ''),
  );

  const start = (page - 1) * PAGE_SIZE;
  return {
    list: filtered.slice(start, start + PAGE_SIZE),
    currentPage: page,
    pageSize: PAGE_SIZE,
    totalCount: filtered.length,
  };
};

export const getClients = async (req: GetClientsRequest = {}): Promise<ClientsResponse> => {
  if (!apiEnabled) return getClientsMock(req);

  // Фильтр городов в UI живёт именами — бэк ждёт id из справочника
  let cityIds: string[] | undefined;
  if (req.cities?.length) {
    const refs = await getCities();
    cityIds = req.cities
      .map((name) => refs.find((c) => c.name === name)?.id)
      .filter((id): id is string => id !== undefined);
  }

  const raw = await http<Record<string, unknown>>('admin/clients', {
    params: {
      type: req.type,
      status: req.status,
      query: req.query?.trim() || undefined,
      cities: cityIds?.length ? cityIds : undefined,
      tariffs: req.tariffs?.length ? req.tariffs : undefined,
      page: req.page,
    },
    skipCaseConversion: true,
  });

  return {
    list: (Array.isArray(raw.list) ? raw.list : []).map(toClientListItem),
    currentPage: typeof raw.currentPage === 'number' ? raw.currentPage : 1,
    pageSize: typeof raw.pageSize === 'number' ? raw.pageSize : PAGE_SIZE,
    totalCount: typeof raw.totalCount === 'number' ? raw.totalCount : 0,
  };
};
